package com.meistercharts.charts.pv.modulePlanning

import com.meistercharts.annotations.Window
import com.meistercharts.annotations.Zoomed
import com.meistercharts.canvas.MouseCursor
import com.meistercharts.canvas.interaction.HandleRole
import com.meistercharts.canvas.interaction.InteractionState
import com.meistercharts.canvas.interaction.InteractionTarget
import com.meistercharts.canvas.interaction.InteractionTool
import com.meistercharts.canvas.interaction.ToolReaction
import com.meistercharts.charts.pv.roofPlanning.Clickable
import com.meistercharts.charts.pv.roofPlanning.DeleteModuleArea
import com.meistercharts.charts.pv.roofPlanning.DeleteUnusableArea
import com.meistercharts.charts.pv.roofPlanning.Module
import com.meistercharts.charts.pv.roofPlanning.ModuleArea
import com.meistercharts.charts.pv.roofPlanning.MoveModuleArea
import com.meistercharts.charts.pv.roofPlanning.MoveUnusableArea
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningModel
import com.meistercharts.charts.pv.roofPlanning.ResizeElement
import com.meistercharts.charts.pv.roofPlanning.RoofPlanningCommand
import com.meistercharts.charts.pv.roofPlanning.RoofRelative
import com.meistercharts.charts.pv.roofPlanning.RotateModuleArea
import com.meistercharts.charts.pv.roofPlanning.ToggleModuleDeleted
import com.meistercharts.charts.pv.roofPlanning.UnusableArea
import com.meistercharts.charts.pv.roofPlanning.resizableOrNull
import com.meistercharts.charts.pv.roofPlanning.resizable
import it.neckar.events.KeyCode
import it.neckar.events.KeyStroke
import it.neckar.geometry.Coordinates
import it.neckar.geometry.Distance
import it.neckar.geometry.Rectangle
import it.neckar.geometry.Size
import it.neckar.open.kotlin.lang.requireNotNull
import it.neckar.open.unit.si.mm

/**
 * Places, moves, resizes, rotates and deletes the elements of one roof.
 *
 * Which of those a press does is decided by the [HandleRole] of the region it hits, not by a mode the user has to pick
 * beforehand: pressing the body of an element drags it, pressing its delete button deletes it.
 */
class RoofPlanningTool(
  /**
   * Converts a distance on screen into a distance on the roof. The tool works in roof coordinates so it never needs the
   * chart calculator.
   */
  val toRoofDistance: (@Zoomed Distance) -> @RoofRelative @mm Distance,
) : InteractionTool<Clickable, PvRoofPlanningModel> {

  override val description: String = "Modulbelegung planen"

  override fun onHover(
    target: InteractionTarget<Clickable>?,
    location: @Window Coordinates?,
    state: InteractionState<Clickable>,
    model: PvRoofPlanningModel,
  ): ToolReaction<Clickable, PvRoofPlanningModel> {
    if (state.changingGeometry) {
      //A drag or resize is in progress - the pointer belongs to that gesture
      return ToolReaction(state)
    }

    return ToolReaction(state.copy(hovered = target))
  }

  override fun onPress(
    target: InteractionTarget<Clickable>?,
    location: @Window Coordinates,
    state: InteractionState<Clickable>,
    model: PvRoofPlanningModel,
  ): ToolReaction<Clickable, PvRoofPlanningModel> {
    //A press begins a gesture and therefore ends the one before it - a release outside the canvas never reaches the tool
    val pressed = state.copy(drag = null, resized = null)

    val element = target?.element
      //A press next to everything clears the selection, but does not consume the event: layers below may still use it
      ?: return ToolReaction.ignored(pressed.copy(hovered = null, selected = null))

    return when (target.role) {
      HandleRole.DeleteButton -> ToolReaction(
        state = pressed.copy(hovered = null, selected = null),
        commands = listOfNotNull(deleteCommandFor(element)),
      )

      HandleRole.RotateButton -> when (element) {
        is ModuleArea -> ToolReaction(pressed.copy(selected = element), listOf(RotateModuleArea(element)))
        is Module, is UnusableArea -> ToolReaction(pressed.copy(selected = element))
      }

      HandleRole.Body -> ToolReaction(pressed.copy(selected = element, drag = InteractionState.Drag(element, element.resizable().location)))

      HandleRole.AddButton, HandleRole.ResizeHandle -> ToolReaction(pressed.copy(selected = element))
    }
  }

  override fun onDrag(
    totalDistance: @Zoomed Distance,
    state: InteractionState<Clickable>,
    model: PvRoofPlanningModel,
  ): ToolReaction<Clickable, PvRoofPlanningModel> {
    val drag = state.drag ?: return ToolReaction.ignored(state)
    @RoofRelative val draggedTo: Coordinates = drag.from + toRoofDistance(totalDistance)

    return when (val resizable = drag.element.resizable()) {
      is ModuleArea -> ToolReaction(
        state = state,
        commands = listOf(MoveModuleArea(resizable, model.clampModuleAreaLocation(resizable, draggedTo))),
      )

      is UnusableArea -> ToolReaction(
        state = state,
        commands = listOf(MoveUnusableArea(resizable, model.clampUnusableAreaLocation(resizable, draggedTo))),
      )
    }
  }

  override fun onRelease(
    state: InteractionState<Clickable>,
    model: PvRoofPlanningModel,
  ): ToolReaction<Clickable, PvRoofPlanningModel> {
    return ToolReaction(state.copy(drag = null, resized = null))
  }

  override fun onKey(
    keyStroke: KeyStroke,
    state: InteractionState<Clickable>,
    model: PvRoofPlanningModel,
  ): ToolReaction<Clickable, PvRoofPlanningModel> {
    if (keyStroke != KeyStroke(KeyCode.Delete)) {
      return ToolReaction.ignored(state)
    }

    val selected = state.selected ?: return ToolReaction.ignored(state)
    val command = deleteCommandFor(selected) ?: return ToolReaction.ignored(state)

    //A toggled module stays where it is, so a drag of it survives - a resize does not: its handles go with the selection
    val remainingDrag = if (command is ToggleModuleDeleted) state.drag else null

    return ToolReaction(state.copy(selected = null, hovered = null, drag = remainingDrag, resized = null), listOf(command))
  }

  /**
   * Starts a resize of the selected element. The gesture is anchored on the element's geometry now, so every event of it
   * is applied to that anchor rather than to the geometry the gesture has already produced.
   */
  fun startResizing(state: InteractionState<Clickable>): ToolReaction<Clickable, PvRoofPlanningModel> {
    val resizable = state.selected.resizableOrNull().requireNotNull { "Cannot start resizing without a selected resizable element" }

    return ToolReaction(state.copy(resized = InteractionState.Resize(resizable, Rectangle(resizable.location, resizable.size))))
  }

  /**
   * Applies a resize to the element that is currently being resized.
   */
  fun resizeTo(
    newLocation: @RoofRelative Coordinates,
    newSize: @mm Size,
    state: InteractionState<Clickable>,
  ): ToolReaction<Clickable, PvRoofPlanningModel> {
    val resized = state.resizedElement.requireNotNull { "Cannot resize without a resize in progress" }

    return ToolReaction(state, listOf(ResizeElement(resized.resizable(), newLocation, newSize)))
  }

  override fun cursorFor(state: InteractionState<Clickable>): MouseCursor? {
    if (state.drag != null) {
      return MouseCursor.Move
    }

    return when (state.armedRole) {
      HandleRole.DeleteButton, HandleRole.RotateButton, HandleRole.AddButton -> MouseCursor.Hand
      HandleRole.Body, HandleRole.ResizeHandle, null -> null
    }
  }

  private fun deleteCommandFor(element: Clickable): RoofPlanningCommand? {
    return when (element) {
      is UnusableArea -> DeleteUnusableArea(element)
      is ModuleArea -> DeleteModuleArea(element)
      is Module -> ToggleModuleDeleted(element)
    }
  }
}

/**
 * Keeps a location for the module area inside the suggested roof insets.
 */
private fun PvRoofPlanningModel.clampModuleAreaLocation(moduleArea: ModuleArea, location: @RoofRelative Coordinates): @RoofRelative Coordinates {
  return location.coerceIn(
    minimum = suggestedRoofInsets.bottomLeft,
    maximumX = roofSize.width - suggestedRoofInsets.right - moduleArea.size.width,
    maximumY = roofSize.height - suggestedRoofInsets.top - moduleArea.size.height,
  )
}

/**
 * Keeps a location for the unusable area on the roof.
 */
private fun PvRoofPlanningModel.clampUnusableAreaLocation(unusableArea: UnusableArea, location: @RoofRelative Coordinates): @RoofRelative Coordinates {
  return location.coerceIn(
    minimum = Coordinates.origin,
    maximumX = roofSize.width - unusableArea.size.width,
    maximumY = roofSize.height - unusableArea.size.height,
  )
}
